import {
  faFileExport,
  faFilter,
  faPlus
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import DatePicker from 'components/base/DatePicker';
import SearchBox from 'components/common/SearchBox';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useState, ChangeEvent, useEffect } from 'react';
import { Col, Row, Toast, ToastContainer } from 'react-bootstrap';
import apiClient, { handleAPIError } from 'helpers/apiService';
import { useAuth } from 'providers/AuthProvider';
import { UserData } from './components/DataDefinitions';
import { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';
import FeatherIcon from 'feather-icons-react';
import RevealDropdown, { RevealDropdownTrigger } from 'components/base/RevealDropdown';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import { useNavigate } from "react-router-dom";
import { Dropdown } from 'react-bootstrap';

import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import Badge from 'components/base/Badge';

const breadcrumbItems: PageBreadcrumbItem[] = [
  {
    label: 'Dashboard',
    url: '/dashboard/home'
  },
  {
    label: 'Users (Admin)',
    active: true
  }
];

const ActionDropdownItems = ({ user_id, onDeleteUser }: {
  user_id: string | number,
  onDeleteUser: (user_id: string | number) => void;
}) => {
  return (
    <>
      <Dropdown.Item eventKey="2" onClick={() => onDeleteUser(user_id)} className="text-danger">
        Remove
      </Dropdown.Item>
    </>
  );
};

const UsersTable = () => {
  return (
    <div className="border-top">
      <AdvanceTable
        tableProps={{ className: 'phoenix-table fs-9' }}
        rowClassName="hover-actions-trigger btn-reveal-trigger"
      />
      <AdvanceTableFooter pagination className="py-4" />
    </div>
  );
};



const AdminUsers = () => {
  const auth = useAuth();
  const [users, setUsers] = useState<UserData[]>([]);

  // State to control Toast messages
  const [show, setShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const usersTableColumns: ColumnDef<UserData>[] = [
    /*
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row: { original } }) => {
        const {
          first_name, last_name,
          role
        } = original;


        return (

          <div className="d-flex align-items-center">

            <div>
              <Link to={`/admin/users/update/${original.user_id}`} className="fs-8 fw-bold">
                {first_name + " " + last_name}
              </Link>
              <div className="d-flex align-items-center">
                <p className="mb-0 text-1000 fw-semi-bold fs-9 me-2">
                  {role}
                </p>
              </div>
            </div>
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '25%' }, className: 'ps-0' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    */
    {
      accessorKey: 'login',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-success-100 rounded me-2">
              <FeatherIcon
                icon="mail"
                size={16}
                className="text-success-600 dark__text-success-300"
              />
            </div>
            <span>Login</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { login } = original;
        return (
          <Link to={`/admin/users/update/${original.user_id}`} className="fs-8 fw-bold">
            {login}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      header: 'First name',
      accessorKey: 'first_name',
      meta: {
        cellProps: { className: 'ps-3 text-900 py-4' },
        headerProps: { style: { width: '12%' }, className: 'ps-3' }
      }
    },
    {
      header: 'Last name',
      accessorKey: 'last_name',
      meta: {
        cellProps: { className: 'ps-3 text-900 py-4' },
        headerProps: { style: { width: '12%' }, className: 'ps-3' }
      }
    },
    {
      accessorKey: 'customer_name',
      header: () => {
        return (
          <div className="d-inline-flex flex-center">
            <div className="px-1 py-1 bg-primary-100 rounded me-2">
              <FeatherIcon
                icon="phone"
                size={16}
                className="text-primary-600 dark__text-primary-300"
              />
            </div>
            <span>customer_name</span>
          </div>
        );
      },
      cell: ({ row: { original } }) => {
        const { customer } = original;
        return (
          <Link className="text-1000" to={`${customer.customer_name}`}>
            {customer.customer_name}
          </Link>
        );
      },
      meta: {
        headerProps: {
          style: { width: '15%', minWidth: '180px' },
          className: 'ps-4 pe-5 border-end'
        },
        cellProps: {
          className: 'white-space-nowrap fw-semi-bold ps-4 border-end'
        }
      }
    },
    {
      accessorKey: 'disabled',
      header: 'Disabled',
      cell: ({ row: { original } }) => {
        return (
          <Badge variant="phoenix" bg={original.disabled ? 'success' : 'warning'} className="mx-2">
            {original.disabled ? 'Yes' : 'No'}
          </Badge>
        );
      },
      meta: {
        headerProps: { style: { width: '10%' }, className: 'ps-4 align-content-center' },
        cellProps: { className: 'white-space-nowrap ps-0' }
      }
    },
    {
      accessorKey: 'inserted_at',
      header: 'Create date',
      cell: ({ row: { original } }) => original.inserted_at,
      meta: {
        headerProps: {
          style: { width: '15%' },
          className: 'ps-4 pe-5'
        },
        cellProps: {
          className: 'white-space-nowrap ps-4 text-700'
        }
      }
    },
    {
      id: 'contactDropdown',
      cell: ({ row: { original } }) => {
        return (
          <RevealDropdownTrigger>
            <RevealDropdown>
              <ActionDropdownItems user_id={original.user_id} onDeleteUser={deleteUser} />
            </RevealDropdown>
          </RevealDropdownTrigger>
        );
      },
      meta: {
        cellProps: {
          className: 'text-end pe-0 ps-4'
        }
      }
    }
  ];

  const fetchData = async () => {
    const response = await apiClient.get(
      '/v1/admin/users',
      {
        headers: {
          'accept': 'application/json',

        },
        withCredentials: true,
      }
    );
    setUsers(response.data);
  };

  useEffect(() => {
    fetchData();
  }, []);


  const table = useAdvanceTable({
    data: users,
    columns: usersTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true,
    initialState: {
      columnVisibility: {
        status: false,
        designation: false
      }
    }
  });
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  const navigate = useNavigate();

  const handleAddUserClick = () => {
    navigate('/admin/users/add'); // Navigate to the AddContact page
  };

  const deleteUser = async (user_id: string | number) => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      const response = await apiClient.delete(
        `/v1/admin/users/${user_id}`,
        {
          headers: {
            'accept': 'application/json',

          }
        }
      );
      setToastMessage(response.data['message'])
      setShow(true);
      fetchData();
    }
  };

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div>
        <ToastContainer className="p-3" position='top-center'>
          <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
            <Toast.Body className=''>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </div>
      <div className="mb-6">
        <AdvanceTableProvider {...table}>
          <h2 className="mb-4">{users.length} Users</h2>
          <Row className="g-3 justify-content-between mb-4">
            <Col xs="auto">
              <Button
                variant="primary"
                className="me-4"
                startIcon={<FontAwesomeIcon icon={faPlus} className="me-2" />}
                onClick={handleAddUserClick}
              >
                Create User
              </Button>
              <Button
                variant="link"
                className="text-900 px-0"
                startIcon={
                  <FontAwesomeIcon icon={faFileExport} className="fs-9 me-2" />
                }
              >
                Export
              </Button>
            </Col>
            <Col xs="auto">
              <div className="d-flex">
                <SearchBox
                  placeholder="Search by name"
                  className="me-2"
                  onChange={handleSearchInputChange}
                />
                <DatePicker defaultValue="Mar 1, 2022" />
                <Button
                  variant="phoenix-secondary"
                  className="px-3 ms-2"
                  onClick={() => setOpenFilterModal(true)}
                >
                  <FontAwesomeIcon
                    icon={faFilter}
                    transform="down-3"
                    className="text-primary"
                  />
                </Button>
              </div>
              <LeadsFilterModal
                show={openFilterModal}
                handleClose={() => setOpenFilterModal(false)}
              />
            </Col>
          </Row>
          <UsersTable />
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default AdminUsers;